import { useEffect, useRef, useState } from 'react';
import Skeleton from '@/common/components/skeleton/Skeleton';
import useImageLoader, { normalizeFileId } from '@/common/hooks/useImage';
import useVideoLoader from '@/common/hooks/useVideo';
import { playVideoSafely } from '@/common/functions/videoElement';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { formatTranslation } from '@/common/utils/formatTranslation';
import {
	Bio,
	PersonCard,
	PersonImage,
	PersonInfo,
	PersonMediaSkeleton,
	PersonShade,
	PersonTitle,
	PersonVideo,
	ReloadButton,
} from './profileCard.styles';

const ReloadIcon = ({ size = 22 }) => (
	<svg
		width={size}
		height={size}
		viewBox='0 0 24 24'
		fill='none'
		stroke='white'
		strokeWidth='2'
		strokeLinecap='round'
		strokeLinejoin='round'
	>
		<polyline points='23 4 23 10 17 10' />
		<polyline points='1 20 1 14 7 14' />
		<path d='M3.51 9a9 9 0 0114.13-3.36L23 10' />
		<path d='M20.49 15a9 9 0 01-14.13 3.36L1 14' />
	</svg>
);

const getProfileDirectSrc = (profile) => {
	const profileImage = profile?.profile_image;

	if (profileImage && typeof profileImage === 'object') {
		return profileImage.base64 || profileImage.url || null;
	}

	return profile?.image || null;
};

const ProfileCard = ({ profile, onClick, onReload, discover = false }) => {
	const t = useLanguageData('profileCard');
	const profileMediaId = normalizeFileId(
		profile?.profile_image?._id ?? profile?.profile_image,
	);
	const directImageSrc = getProfileDirectSrc(profile);
	const { imageSrc, isLoading } = useImageLoader(profileMediaId);
	const resolvedImageSrc = imageSrc || directImageSrc;
	const [isImageLoaded, setIsImageLoaded] = useState(false);
	const [hasImageError, setHasImageError] = useState(false);
	const imageRef = useRef(null);

	const isFileReady = !profileMediaId || !isLoading;
	const showImageSkeleton =
		!isFileReady || !resolvedImageSrc || hasImageError || !isImageLoaded;

	useEffect(() => {
		setIsImageLoaded(false);
		setHasImageError(false);
	}, [resolvedImageSrc]);

	useEffect(() => {
		const element = imageRef.current;

		if (!element || !resolvedImageSrc || hasImageError) {
			return;
		}

		if (element.complete && element.naturalWidth > 0) {
			setIsImageLoaded(true);
		}
	}, [resolvedImageSrc, hasImageError]);

	const videoMediaId = normalizeFileId(
		profile?.profile_video?._id ?? profile?.profile_video,
	);
	const { videoSrc } = useVideoLoader(videoMediaId);
	const videoRef = useRef(null);

	const hasVideo = Boolean(videoSrc);

	const handleImageLoad = () => {
		setIsImageLoaded(true);
		setHasImageError(false);
	};

	const handleImageError = () => {
		setIsImageLoaded(false);
		setHasImageError(true);
	};

	const handleMouseEnter = () => {
		playVideoSafely(videoRef.current);
	};

	const handleMouseLeave = () => {
		if (videoRef.current) {
			videoRef.current.pause();
			videoRef.current.currentTime = 0;
		}
	};

	return (
		<PersonCard
			$discover={discover}
			$hasVideo={hasVideo}
			onClick={() => onClick(profile)}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onClick(profile);
				}
			}}
			role='button'
			tabIndex={0}
		>
			{showImageSkeleton && (
				<PersonMediaSkeleton>
					<Skeleton
						width='100%'
						height='100%'
						radius='0'
						minWidth='100%'
						minHeight='100%'
					/>
				</PersonMediaSkeleton>
			)}
			{resolvedImageSrc && !hasImageError && (
				<PersonImage
					ref={imageRef}
					src={resolvedImageSrc}
					alt={formatTranslation(t.portraitAlt, { name: profile.name })}
					$visible={isImageLoaded}
					onLoad={handleImageLoad}
					onError={handleImageError}
				/>
			)}
			{hasVideo && (
				<PersonVideo
					ref={videoRef}
					key={`${videoMediaId}-${videoSrc}`}
					src={videoSrc}
					muted
					loop
					playsInline
					preload='auto'
					draggable={false}
					poster={isImageLoaded ? resolvedImageSrc : undefined}
				/>
			)}
			<PersonShade />
			<PersonInfo>
				<PersonTitle>
					<h3>
						{profile.name}
						{profile.age ? <span>{profile.age}</span> : null}
					</h3>
				</PersonTitle>
				{(profile.description || profile.person_config?.description) && (
					<Bio>{profile.description || profile.person_config?.description}</Bio>
				)}
			</PersonInfo>
		</PersonCard>
	);
};

export default ProfileCard;
